import { Component, OnInit, Input, Output, ElementRef, EventEmitter, ContentChildren, QueryList, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormControl } from '@angular/forms';
import { CfCoreComponent } from '../core/core.component';
import { CfSelectableComponent } from '../selectable/selectable.component';
import { CfRadioComponent } from '../radio/radio.component';
import { GroupModel } from '../../models/group/group.model';
import { GroupStylingModel } from '../../models/group/group-styling.model';
import { GroupTemplates } from '../../templates/group.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Group Component</p>
 * <pre>
 * <code>
 * import {{'{'}} GroupModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} GroupStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-group<code>></code><code><</code><code>/</code>cf-group<code>></code>
 * </code>
 * </pre>
 */
export class CfGroupComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden
 */
selectables: QueryList<CfSelectableComponent>;
/**
 * @hidden
 */
radios: QueryList<CfRadioComponent>;
/**
 * @hidden
 */
itemsSelected: number[] = [];
/**
 * @hidden
 */
valuesSelected: any[] = [];
/**
 * @hidden
 */
randomId = 0;
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  label: 'My favourite fruits',
 *  minSelected: 1,
 *  maxSelected: 5,
 *  allSelected: false
 * {{'}'}}</pre></code>
 * <h4>label: string</h4>
 * Component label
 * <h4>minSelected: number</h4>
 * Number of minimum selected items
 * <h4>maxSelected: number</h4>
 * Number of maximum selected items
 * <h4>allSelected: boolean</h4>
 * If all items must be selected
 */
public properties: GroupModel;
/**
 * <p>Styling object:</p>
 * <h4>container: StylingModel</h4>
 * Styling properties for main container
 * <h4>label: StylingModel</h4>
 * Styling for the group label
 * <h4>content: StylingModel</h4>
 * Styling for the group content
 */
public styling: GroupStylingModel;
/**
 * Component label
 * @param {?} value
 * @return {?}
 */
set label(value: string) { this.properties.label = value; }
/**
 * Number of minimum selected items
 * @param {?} value
 * @return {?}
 */
set minSelected(value: number) { this.properties.minSelected = value; }
/**
 * Number of maximum selected items
 * @param {?} value
 * @return {?}
 */
set maxSelected(value: number) { this.properties.maxSelected = value; }
/**
 * If all items must be selected
 * @param {?} value
 * @return {?}
 */
set allSelected(value: boolean) { this.properties.allSelected = value; }
/**
 * Event when item selection was toggled
 */
onSelect = new EventEmitter();
/**
 * @hidden
 * @param {?} obj
 * @return {?}
 */
writeValue(obj: any): void {
    if (obj) {
      this.valuesSelected = [].concat(obj);
      this.itemsSelected = [];
      if (this.selectables != null && this.selectables.length > 0) {
        for (let /** @type {?} */ userValue of this.valuesSelected) {
          for (let /** @type {?} */ selectable of this.selectables.toArray()) {
            if (selectable.properties.value === userValue) {
              selectable.updateFromGroup(true);
              this.itemsSelected.push(selectable.internalId);
              break;
            }
          }
        }
        this.itemsSelected.sort();
      } else if (this.radios != null && this.radios.length > 0) {
        let /** @type {?} */ userValue = this.valuesSelected[0];
        for (let /** @type {?} */ radio1 of this.radios.toArray()) {
          if (radio1.properties.value === userValue) {
            radio1.updateFromGroup(true);
            this.itemsSelected.push(radio1.internalId);
            break;
          }
        }
        this.itemsSelected.sort();
      }
    }
  }
/**
 * @hidden
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: any): void {
    this.onChange = (value) => { Promise.resolve(null).then(() => fn(value)) };
  }
/**
 * @hidden
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: any): void {
    this.onTouched = (value) => { Promise.resolve(null).then(() => fn(value)) };
  }
/**
 * @hidden
 * @param {?} isDisabled
 * @return {?}
 */
setDisabledState(isDisabled: boolean): void {
  }
/**
 * @hidden
 */
onChange: any = () => { };
/**
 * @hidden
 */
onTouched: any = () => { };
/**
 * @hidden
 * @return {?}
 */
public resetGroup() {
    if (this.selectables != null && this.selectables.length > 0) {
      this.itemsSelected.forEach(item => {
        this.selectables.toArray()[item].updateFromGroup(false);
      });
    } else if (this.radios != null && this.radios.length > 0) {
      this.itemsSelected.forEach(item => {
        this.radios.toArray()[item].updateFromGroup(false);
      });
    }
    this.itemsSelected = [];
    this.valuesSelected = [];
    this.onSelect.emit(this.itemsSelected);
    this.onChange(this.checkOutput(this.valuesSelected));
    this.onTouched();
  }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new GroupModel();
    this.styling = new GroupStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    super.ngOnInit();
    this.getMyTemplate('group', GroupTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'GROUP').then(sampler => {
        // console.log('GROUP', this);
      });
    });
  }
/**
 * @hidden
 * @return {?}
 */
ngAfterContentInit() {
    if (this.selectables != null && this.selectables.length > 0) {
      for (let /** @type {?} */ selectable of this.selectables.toArray()) {
        selectable.internalId = this.randomId;
        if (selectable.checked === true) {
          this.itemsSelected.push(this.randomId);
          this.valuesSelected.push(selectable.properties.value);
        }
        this.randomId++;
        selectable.clicked.subscribe(data => this.updateValues(data));
      }
      if (this.properties.maxSelected == null) {
        this.properties.maxSelected = this.properties.allSelected ? this.selectables.length : this.properties.minSelected;
      }

    } else if (this.radios != null && this.radios.length > 0) {
      for (let /** @type {?} */ radio1 of this.radios.toArray()) {
        radio1.internalId = this.randomId;
        if (radio1.checked === true) {
          this.itemsSelected.push(this.randomId);
          this.valuesSelected.push(radio1.properties.value);
        }
        this.randomId++;
        radio1.clicked.subscribe(data => this.updateValues(data) );
      }
      this.properties.maxSelected = 1;
      this.properties.minSelected = 1;
    }
    this.writeValue(this.valuesSelected)
  }
/**
 * @hidden
 * @param {?} data
 * @return {?}
 */
updateValues(data: any) {
    if (this.selectables != null && this.selectables.length > 0) {
      if (this.itemsSelected.indexOf(data['id']) >= 0 && data['value'] === true) {
        if (this.properties.minSelected === this.itemsSelected.length) {
          this.selectables.toArray()[data['id']].updateFromGroup(false);
        } else {
          let /** @type {?} */ neededSelectable = this.selectables.toArray()[data['id']];
          neededSelectable.updateFromGroup(true);
          let /** @type {?} */ valueSelected = neededSelectable.properties.value;
          this.valuesSelected.splice(this.valuesSelected.indexOf(valueSelected), 1);
          this.itemsSelected.splice(this.itemsSelected.indexOf(data['id']), 1);
          this.onSelect.emit(this.itemsSelected);
          this.onChange(this.checkOutput(this.valuesSelected));
          this.onTouched();
        }
      } else {
        if (this.itemsSelected.indexOf(data['id']) < 0 && data['value'] === false) {
          if (this.itemsSelected.length >= this.properties.maxSelected) {
            this.selectables.toArray()[this.itemsSelected[0]].updateFromGroup(false);
            this.itemsSelected.splice(0, 1);
            this.valuesSelected.splice(0, 1);
          }
          this.itemsSelected.push(data['id']);
          this.itemsSelected.sort();
          let /** @type {?} */ index = this.itemsSelected.indexOf(data['id']);
          let /** @type {?} */ neededSelectable = this.selectables.toArray()[data['id']];
          let /** @type {?} */ valueSelected = neededSelectable.properties.value;
          this.valuesSelected.splice(index, 0, valueSelected);
          this.onSelect.emit(this.itemsSelected);
          this.onChange(this.checkOutput(this.valuesSelected));
          this.onTouched();
        }
      }
    } else if (this.radios != null && this.radios.length > 0) {
      if (data['value'] === true) {
        if (this.itemsSelected.length >= this.properties.maxSelected) { // I don't exist and max is full uncheck first and check me
          this.radios.toArray()[this.itemsSelected[0]].updateFromGroup(false);
          this.itemsSelected.splice(0, 1);
          this.valuesSelected.splice(0, 1);
        }
        this.itemsSelected.push(data['id']);
        let /** @type {?} */ neededradio = this.radios.toArray()[data['id']];
        let /** @type {?} */ valueSelected = neededradio.properties.value;
        this.valuesSelected.push(valueSelected);
        this.onSelect.emit(this.itemsSelected);
        this.onChange(this.checkOutput(this.valuesSelected));
        this.onTouched();
      } else {
        if (this.itemsSelected.length > 0) { this.radios.toArray()[this.itemsSelected[0]].updateFromGroup(true); }
      }
    }
  }
/**
 * @hidden
 * @param {?} values
 * @return {?}
 */
checkOutput(values) {
    if (this.selectables != null && this.selectables.length > 0 || this.radios != null && this.radios.length > 0) {
      return values;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-group',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-group-container'\"> <h5 *ngIf=\"!!properties.label\" class=\"{{activeTemplate.style?.label?.class}} {{styling?.label?.class}} {{getDynamicClasses(activeTemplate.style?.label?.dynamicClass)}} {{getDynamicClasses(styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-group-label'\">{{properties.label}}</h5> <div class=\"{{activeTemplate.style?.content?.class}} {{styling?.content?.class}} {{getDynamicClasses(activeTemplate.style?.content?.dynamicClass)}} {{getDynamicClasses(styling?.content?.dynamicClass)}}\" [ngClass]=\"'cf-group-content'\"><ng-content></ng-content></div> </div>",
  styles: [""],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CfGroupComponent),
      multi: true
    }
  ]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'selectables': [{ type: ContentChildren, args: [CfSelectableComponent, ] },],
'radios': [{ type: ContentChildren, args: [CfRadioComponent, ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'label': [{ type: Input, args: ['label', ] },],
'minSelected': [{ type: Input, args: ['minSelected', ] },],
'maxSelected': [{ type: Input, args: ['maxSelected', ] },],
'allSelected': [{ type: Input, args: ['allSelected', ] },],
'onSelect': [{ type: Output },],
};
}

function CfGroupComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfGroupComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfGroupComponent.ctorParameters;
/** @type {?} */
CfGroupComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfGroupComponent.prototype.selectables;
/**
 * @hidden
 * @type {?}
 */
CfGroupComponent.prototype.radios;
/**
 * @hidden
 * @type {?}
 */
CfGroupComponent.prototype.itemsSelected;
/**
 * @hidden
 * @type {?}
 */
CfGroupComponent.prototype.valuesSelected;
/**
 * @hidden
 * @type {?}
 */
CfGroupComponent.prototype.randomId;
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  label: 'My favourite fruits',
 *  minSelected: 1,
 *  maxSelected: 5,
 *  allSelected: false
 * {{'}'}}</pre></code>
 * <h4>label: string</h4>
 * Component label
 * <h4>minSelected: number</h4>
 * Number of minimum selected items
 * <h4>maxSelected: number</h4>
 * Number of maximum selected items
 * <h4>allSelected: boolean</h4>
 * If all items must be selected
 * @type {?}
 */
CfGroupComponent.prototype.properties;
/**
 * <p>Styling object:</p>
 * <h4>container: StylingModel</h4>
 * Styling properties for main container
 * <h4>label: StylingModel</h4>
 * Styling for the group label
 * <h4>content: StylingModel</h4>
 * Styling for the group content
 * @type {?}
 */
CfGroupComponent.prototype.styling;
/**
 * Event when item selection was toggled
 * @type {?}
 */
CfGroupComponent.prototype.onSelect;
/**
 * @hidden
 * @type {?}
 */
CfGroupComponent.prototype.onChange;
/**
 * @hidden
 * @type {?}
 */
CfGroupComponent.prototype.onTouched;
/** @type {?} */
CfGroupComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
